import { useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  Close,
  CloudUpload,
  FolderZip,
  InsertDriveFile,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";
import { formatBytes } from "components/preview/previewTypes";

export type UploadItemStatus = "queued" | "uploading" | "uploaded" | "failed";

export interface UploadItem {
  file: File;
  status: UploadItemStatus;
  s3Key?: string;
  error?: string;
}

interface PdfFilesDropzoneProps {
  items: UploadItem[];
  onAddFiles: (files: File[]) => void;
  onRemove: (name: string) => void;
  disabled?: boolean;
}

export default function PdfFilesDropzone({
  items,
  onAddFiles,
  onRemove,
  disabled = false,
}: PdfFilesDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (list: FileList | null) => {
    if (!list || disabled) return;
    onAddFiles(Array.from(list));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => {
          if (!disabled) fileInputRef.current?.click();
        }}
        sx={{
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          borderRadius: 2,
          p: 2.5,
          textAlign: "center",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.6 : 1,
          bgcolor: dragOver ? "action.hover" : "background.default",
          transition: "border-color 0.15s ease, background-color 0.15s ease",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept=".zip,.pdf,application/zip,application/pdf"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <CloudUpload sx={{ fontSize: 34, color: "text.secondary" }} />
        <Typography sx={{ mt: 0.5 }}>
          Drag &amp; drop a ZIP archive here, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upload the ZIP with the client&apos;s PDFs (e.g. downloaded from their
          OneDrive). Plain PDF files are also accepted.
        </Typography>
      </Box>

      {items.map((item) => (
        <Box
          key={item.file.name}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          {item.file.name.toLowerCase().endsWith(".zip") ? (
            <FolderZip color="primary" sx={{ flexShrink: 0 }} />
          ) : (
            <InsertDriveFile color="primary" sx={{ flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>
              {item.file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatBytes(item.file.size)}
              {item.status === "failed" && item.error ? ` — ${item.error}` : ""}
            </Typography>
            {item.status === "uploading" && (
              <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />
            )}
          </Box>
          {item.status === "uploading" ? (
            <CircularProgress size={18} sx={{ flexShrink: 0 }} />
          ) : item.status === "uploaded" ? (
            <CheckCircle color="success" sx={{ fontSize: 20, flexShrink: 0 }} />
          ) : item.status === "failed" ? (
            <ErrorOutline color="error" sx={{ fontSize: 20, flexShrink: 0 }} />
          ) : null}
          <IconButton
            size="small"
            onClick={() => onRemove(item.file.name)}
            disabled={disabled || item.status === "uploading"}
            aria-label={`Remove ${item.file.name}`}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}
